import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';
import { type Language } from '../i18n/translations';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 20px;
  color: #444;
`;

const List = styled.ul`
  margin-bottom: 20px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 10px;
  color: #444;
`;

const ContactInfo = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 5px;
  margin-top: 30px;
`;

type PrivacySection = {
  title: string;
  paragraphs?: string[];
  items?: string[];
};

const privacySections: Record<Language, PrivacySection[]> = {
  zh: [
    {
      title: '介绍',
      paragraphs: [
        'SheetMate（“我们”）尊重您的隐私，并致力于保护您的个人数据。本政策说明我们如何处理您的个人数据，以及您可以行使的隐私权利。',
        'SheetMate 用于在 Google Sheets 与 Lark Sheets 中预览单元格内容，包括 Markdown、LaTeX 公式、JSON、链接和图片预览。'
      ]
    },
    {
      title: '我们收集的数据',
      items: [
        '账户信息：当您创建账户时，我们会收集您的电子邮件地址和用户名。',
        '身份验证数据：我们使用 Firebase Authentication 进行用户身份验证。',
        '订阅信息：如果您订阅付费服务，我们会通过 Stripe 处理必要的付款信息。',
        '使用与技术数据：包括功能使用、错误报告、浏览器类型和设备信息。'
      ]
    },
    {
      title: '数据使用方式',
      items: [
        '提供、维护和改进 SheetMate 功能。',
        '处理订阅付款并管理您的账户。',
        '发送服务相关通知并提供客户支持。',
        '检测、预防和解决技术问题。'
      ]
    },
    {
      title: '数据存储与共享',
      paragraphs: [
        '我们使用 Google Firebase 存储必要数据，并采用行业标准的安全措施。我们仅在提供服务、遵守法律义务或保护 SheetMate 与用户安全所需时，与 Firebase、Stripe 等服务提供商共享必要数据。'
      ]
    },
    {
      title: '您的权利与联系我们',
      paragraphs: [
        '根据您所在地区适用法律，您可以请求访问、更正、删除、限制处理或导出您的个人数据。',
        '如果您对本隐私政策有任何疑问，请通过 sheetmate.ai@gmail.com 联系我们。'
      ]
    }
  ],
  en: [
    {
      title: 'Introduction',
      paragraphs: [
        'SheetMate (“we” or “us”) respects your privacy and is committed to protecting your personal data. This policy explains how we handle your data and the privacy rights available to you.',
        'SheetMate helps preview spreadsheet cell content in Google Sheets and Lark Sheets, including Markdown, LaTeX formulas, JSON, links, and images.'
      ]
    },
    {
      title: 'Data We Collect',
      items: [
        'Account information: email address and username when you create an account.',
        'Authentication data: Firebase Authentication is used for sign-in.',
        'Subscription information: Stripe processes the payment data required for paid services.',
        'Usage and technical data: feature usage, error reports, browser type, and device information.'
      ]
    },
    {
      title: 'How We Use Data',
      items: [
        'Provide, maintain, and improve SheetMate features.',
        'Process subscription payments and manage your account.',
        'Send service-related notices and provide customer support.',
        'Detect, prevent, and address technical issues.'
      ]
    },
    {
      title: 'Storage and Sharing',
      paragraphs: [
        'We use Google Firebase for necessary data storage and apply industry-standard security measures. We share required data with service providers such as Firebase and Stripe only when needed to provide the service, comply with law, or protect SheetMate and our users.'
      ]
    },
    {
      title: 'Your Rights and Contact',
      paragraphs: [
        'Depending on your location, you may request access, correction, deletion, restriction, objection, or portability of your personal data.',
        'For privacy questions or requests, contact us at sheetmate.ai@gmail.com.'
      ]
    }
  ],
  ja: [
    {
      title: 'はじめに',
      paragraphs: [
        'SheetMate（以下「当社」）はユーザーのプライバシーを尊重し、個人データの保護に努めます。本ポリシーでは、データの取り扱いとユーザーの権利について説明します。',
        'SheetMate は Google Sheets と Lark Sheets のセル内容を、Markdown、LaTeX 数式、JSON、リンク、画像などとしてプレビューするための拡張機能です。'
      ]
    },
    {
      title: '収集するデータ',
      items: [
        'アカウント情報：アカウント作成時のメールアドレスとユーザー名。',
        '認証データ：ログインには Firebase Authentication を使用します。',
        'サブスクリプション情報：有料サービスの支払いは Stripe が処理します。',
        '利用・技術データ：機能利用状況、エラーレポート、ブラウザ種別、デバイス情報。'
      ]
    },
    {
      title: 'データの利用目的',
      items: [
        'SheetMate 機能の提供、保守、改善。',
        'サブスクリプション決済とアカウント管理。',
        'サービス関連通知とカスタマーサポート。',
        '技術的問題の検出、予防、対応。'
      ]
    },
    {
      title: '保存と共有',
      paragraphs: [
        '必要なデータは Google Firebase に保存し、業界標準の安全対策を適用します。サービス提供、法令遵守、SheetMate とユーザーの保護に必要な場合のみ、Firebase や Stripe などのサービス提供者と必要なデータを共有します。'
      ]
    },
    {
      title: 'ユーザーの権利と連絡先',
      paragraphs: [
        'お住まいの地域の法律に応じて、個人データのアクセス、訂正、削除、処理制限、異議申し立て、データポータビリティを請求できる場合があります。',
        'プライバシーに関するお問い合わせは sheetmate.ai@gmail.com までご連絡ください。'
      ]
    }
  ],
  id: [
    {
      title: 'Pengantar',
      paragraphs: [
        'SheetMate (“kami”) menghormati privasi Anda dan berkomitmen melindungi data pribadi Anda. Kebijakan ini menjelaskan cara kami menangani data dan hak privasi yang dapat Anda gunakan.',
        'SheetMate membantu mempratinjau isi sel spreadsheet di Google Sheets dan Lark Sheets, termasuk Markdown, rumus LaTeX, JSON, tautan, dan gambar.'
      ]
    },
    {
      title: 'Data yang Kami Kumpulkan',
      items: [
        'Informasi akun: alamat email dan nama pengguna saat Anda membuat akun.',
        'Data autentikasi: Firebase Authentication digunakan untuk proses masuk.',
        'Informasi langganan: Stripe memproses data pembayaran yang diperlukan untuk layanan berbayar.',
        'Data penggunaan dan teknis: penggunaan fitur, laporan kesalahan, jenis browser, dan informasi perangkat.'
      ]
    },
    {
      title: 'Cara Kami Menggunakan Data',
      items: [
        'Menyediakan, memelihara, dan meningkatkan fitur SheetMate.',
        'Memproses pembayaran langganan dan mengelola akun Anda.',
        'Mengirim pemberitahuan terkait layanan dan menyediakan dukungan pelanggan.',
        'Mendeteksi, mencegah, dan menangani masalah teknis.'
      ]
    },
    {
      title: 'Penyimpanan dan Pembagian',
      paragraphs: [
        'Kami menggunakan Google Firebase untuk penyimpanan data yang diperlukan dan menerapkan langkah keamanan standar industri. Kami hanya membagikan data yang diperlukan kepada penyedia layanan seperti Firebase dan Stripe saat diperlukan untuk menyediakan layanan, mematuhi hukum, atau melindungi SheetMate dan pengguna.'
      ]
    },
    {
      title: 'Hak Anda dan Kontak',
      paragraphs: [
        'Bergantung pada lokasi Anda, Anda dapat meminta akses, koreksi, penghapusan, pembatasan, keberatan, atau portabilitas atas data pribadi Anda.',
        'Untuk pertanyaan atau permintaan privasi, hubungi kami di sheetmate.ai@gmail.com.'
      ]
    }
  ],
  es: [
    {
      title: 'Introducción',
      paragraphs: [
        'SheetMate (“nosotros”) respeta tu privacidad y se compromete a proteger tus datos personales. Esta política explica cómo tratamos tus datos y los derechos de privacidad disponibles para ti.',
        'SheetMate ayuda a previsualizar el contenido de celdas de hojas de cálculo en Google Sheets y Lark Sheets, incluidos Markdown, fórmulas LaTeX, JSON, enlaces e imágenes.'
      ]
    },
    {
      title: 'Datos que recopilamos',
      items: [
        'Información de cuenta: dirección de correo electrónico y nombre de usuario cuando creas una cuenta.',
        'Datos de autenticación: usamos Firebase Authentication para el inicio de sesión.',
        'Información de suscripción: Stripe procesa los datos de pago necesarios para los servicios de pago.',
        'Datos de uso y técnicos: uso de funciones, informes de errores, tipo de navegador e información del dispositivo.'
      ]
    },
    {
      title: 'Cómo usamos los datos',
      items: [
        'Proporcionar, mantener y mejorar las funciones de SheetMate.',
        'Procesar pagos de suscripción y gestionar tu cuenta.',
        'Enviar avisos relacionados con el servicio y ofrecer soporte al cliente.',
        'Detectar, prevenir y resolver problemas técnicos.'
      ]
    },
    {
      title: 'Almacenamiento y uso compartido',
      paragraphs: [
        'Usamos Google Firebase para el almacenamiento de datos necesario y aplicamos medidas de seguridad estándar del sector. Solo compartimos los datos necesarios con proveedores de servicios como Firebase y Stripe cuando hace falta para prestar el servicio, cumplir la ley o proteger a SheetMate y a sus usuarios.'
      ]
    },
    {
      title: 'Tus derechos y contacto',
      paragraphs: [
        'Según tu ubicación, puedes solicitar acceso, corrección, eliminación, restricción, oposición o portabilidad de tus datos personales.',
        'Para preguntas o solicitudes de privacidad, contáctanos en sheetmate.ai@gmail.com.'
      ]
    }
  ],
  ms: [
    {
      title: 'Pengenalan',
      paragraphs: [
        'SheetMate (“kami”) menghormati privasi anda dan komited untuk melindungi data peribadi anda. Dasar ini menerangkan cara kami mengendalikan data anda dan hak privasi yang tersedia untuk anda.',
        'SheetMate membantu mempratonton kandungan sel hamparan dalam Google Sheets dan Lark Sheets, termasuk Markdown, rumus LaTeX, JSON, pautan dan imej.'
      ]
    },
    {
      title: 'Data yang Kami Kumpulkan',
      items: [
        'Maklumat akaun: alamat e-mel dan nama pengguna apabila anda membuat akaun.',
        'Data pengesahan: Firebase Authentication digunakan untuk log masuk.',
        'Maklumat langganan: Stripe memproses data pembayaran yang diperlukan untuk perkhidmatan berbayar.',
        'Data penggunaan dan teknikal: penggunaan ciri, laporan ralat, jenis pelayar dan maklumat peranti.'
      ]
    },
    {
      title: 'Cara Kami Menggunakan Data',
      items: [
        'Menyediakan, menyelenggara dan menambah baik ciri SheetMate.',
        'Memproses pembayaran langganan dan mengurus akaun anda.',
        'Menghantar notis berkaitan perkhidmatan dan menyediakan sokongan pelanggan.',
        'Mengesan, mencegah dan menangani isu teknikal.'
      ]
    },
    {
      title: 'Penyimpanan dan Perkongsian',
      paragraphs: [
        'Kami menggunakan Google Firebase untuk penyimpanan data yang diperlukan dan menerapkan langkah keselamatan standard industri. Kami hanya berkongsi data yang diperlukan dengan penyedia perkhidmatan seperti Firebase dan Stripe apabila perlu untuk menyediakan perkhidmatan, mematuhi undang-undang atau melindungi SheetMate dan pengguna kami.'
      ]
    },
    {
      title: 'Hak Anda dan Hubungi Kami',
      paragraphs: [
        'Bergantung pada lokasi anda, anda boleh meminta akses, pembetulan, pemadaman, sekatan pemprosesan, bantahan atau kebolehpindahan data peribadi anda.',
        'Untuk pertanyaan atau permintaan privasi, hubungi kami di sheetmate.ai@gmail.com.'
      ]
    }
  ]
};

const PrivacyPolicy: React.FC = () => {
  const { language, t } = useLanguage();
  const currentDate = new Date().toISOString().split('T')[0];
  const sections = privacySections[language];

  return (
    <Container>
      <Title>{t('privacy.title')}</Title>
      <LastUpdated>{t('privacy.lastUpdated', { date: currentDate })}</LastUpdated>

      {sections.map((section) => (
        <Section key={section.title}>
          <SectionTitle>{section.title}</SectionTitle>
          {section.paragraphs?.map((paragraph) => (
            <Paragraph key={paragraph}>{paragraph}</Paragraph>
          ))}
          {section.items && (
            <List>
              {section.items.map((item) => (
                <ListItem key={item}>{item}</ListItem>
              ))}
            </List>
          )}
        </Section>
      ))}

      <ContactInfo>
        <Paragraph>
          <strong>Email:</strong> sheetmate.ai@gmail.com
        </Paragraph>
      </ContactInfo>
    </Container>
  );
};

export default PrivacyPolicy;
